import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Form,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
} from 'reactstrap';

import axios from 'axios';
import { api } from '../../config';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import ActionButton from '../../Components/Common/ActionButton';
import { Skeleton, SkeletonCard } from '../../Components/Common/Skeleton';
import {
  FormGrid,
  PasswordField,
  RequiredNote,
  focusFirstError,
  hasErrors,
  pattern,
  required,
  validateForm,
  minLength,
} from '../../Components/Form';
import notify from '../../utils/notify';

const PASSWORD_SCHEMA = {
  currentPassword: { label: 'Current password', rules: [required()] },
  newPassword: {
    label: 'New password',
    rules: [
      required(),
      minLength(8, 'New password must be at least 8 characters'),
      pattern(/[A-Z]/, 'New password needs an uppercase letter'),
      pattern(/[!@#$]/, 'New password needs a symbol (! @ # $)'),
    ],
  },
  confirmPassword: {
    label: 'Confirm new password',
    rules: [
      (value, { values }) =>
        values.newPassword !== value ? 'Passwords do not match' : undefined,
    ],
  },
};
const PASSWORD_FIELD_ORDER = [
  'currentPassword',
  'newPassword',
  'confirmPassword',
];

const UserProfile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  const token = useSelector(state => state.Login?.user?.token);

  const fetchProfileData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const response = await axios.get(`${api.API_URL}/profile/me`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      if (response) {
        setProfileData(response);
      } else {
        setError(response?.message || 'Failed to fetch profile data');
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'An error occurred while fetching profile data'
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  const updatePasswordField = (name, e) => {
    const { value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword } = passwordForm;
    const errors = validateForm(passwordForm, PASSWORD_SCHEMA);

    if (hasErrors(errors)) {
      setFormErrors(errors);
      focusFirstError(errors, PASSWORD_FIELD_ORDER);
      return;
    }

    setActionLoading(true);
    try {
      const res = await axios.put(
        `${api.API_URL}/profile/change-password`,
        { currentPassword, newPassword },
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Interceptor kamu langsung mengembalikan string pesan sukses.
      // Jadi kita pakai langsung string tersebut sebagai pesan SweetAlert.
      const successMessage =
        typeof res === 'string'
          ? res
          : res.data?.data || 'Password changed successfully';

      setShowPasswordModal(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      notify.success(successMessage);
    } catch (err) {
      // Tangkap error (seperti password salah / Incorrect password)
      const errRes = err.response?.data ?? err;

      // Karena Interceptor mungkin juga membongkar error, kita cek tipenya
      let errorData = errRes;
      if (typeof errRes === 'string') {
        try {
          errorData = JSON.parse(errRes);
        } catch (e) {
          errorData = { message: errRes };
        }
      }

      let errorMessage =
        'Failed to change the password. Check your internet connection and try again.';
      if (
        errorData?.errors &&
        Array.isArray(errorData.errors) &&
        errorData.errors.length > 0
      ) {
        errorMessage = errorData.errors[0]; // Akan menampilkan: "Incorrect password."
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }

      notify.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  document.title = 'My Profile';

  if (loading) {
    return (
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='My Profile' pageTitle='Account' />
          <Card>
            <CardBody>
              <div className='d-flex align-items-center gap-3'>
                <Skeleton circle width={96} height={96} />
                <div className='flex-grow-1'>
                  <Skeleton width='30%' height={22} />
                  <Skeleton className='mt-2' width='20%' height={14} />
                  <Skeleton className='mt-2' width='40%' height={14} />
                </div>
              </div>
            </CardBody>
          </Card>
          <Row>
            <Col xxl={4}>
              <SkeletonCard lines={3} className='mb-3' />
              <SkeletonCard lines={4} className='mb-3' />
            </Col>
            <Col xxl={8}>
              <SkeletonCard lines={6} />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className='page-content mt-lg-5'>
        <Container fluid>
          <BreadCrumb title='My Profile' pageTitle='Account' />
          <div className='text-center py-5'>
            <i className='ri-error-warning-line display-1 text-warning'></i>
            <h4 className='mt-4'>Unable to load profile</h4>
            <Alert color='danger' className='d-inline-block mt-2'>
              {error}
            </Alert>
            <div className='mt-3'>
              <Button color='primary' onClick={fetchProfileData}>
                <i className='ri-restart-line align-middle me-2'></i>Retry
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className='page-content '>
        <Container fluid>
          <BreadCrumb title='My Profile' pageTitle='Account' />

          <div className='profile-foreground position-relative mx-n4 mt-n4'>
            <div className='profile-wid-bg'>
              <div className='bg-primary' style={{ height: '250px' }}></div>
            </div>
          </div>

          <div className='pt-4 mb-4 mb-lg-3 pb-lg-4'>
            <Row className='g-3 g-lg-4 align-items-center'>
              <Col xs={12} sm='auto' className='text-center text-sm-start'>
                <div className='avatar-lg mx-auto mx-sm-0'>
                  {profileData?.photo ? (
                    <img
                      src={profileData.photo}
                      alt='User avatar'
                      className='img-thumbnail rounded-circle'
                      style={{
                        width: '96px',
                        height: '96px',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <div
                      className='avatar-title rounded-circle bg-light text-primary fs-2 fw-bold'
                      style={{ width: '96px', height: '96px' }}
                    >
                      {profileData?.displayName?.charAt(0) ||
                        profileData?.userName?.charAt(0)}
                    </div>
                  )}
                </div>
              </Col>

              <Col xs={12} sm className='text-center text-sm-start'>
                <div className='p-2'>
                  <h3 className='text-white mb-1'>
                    {profileData?.displayName}
                  </h3>
                  <p className='text-white text-opacity-75 mb-2'>
                    {profileData?.jobTitle || 'No Position'}
                  </p>
                  <div className='d-flex flex-wrap gap-2 text-white-50 justify-content-center justify-content-sm-start'>
                    <div className='d-flex align-items-center'>
                      <i className='ri-map-pin-line me-2 text-white text-opacity-75'></i>
                      <span className='small'>
                        {profileData?.address || 'No Address Set'}
                      </span>
                    </div>
                    {profileData?.department && (
                      <>
                        <div className='d-none d-sm-block'>|</div>
                        <div className='d-flex align-items-center'>
                          <i className='ri-building-4-line me-2 text-white text-opacity-75'></i>
                          <span className='small'>
                            {profileData?.department}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Col>

              <Col xs={12} sm='auto' className='order-sm-last'>
                <div className='d-flex flex-wrap justify-content-center justify-content-sm-end gap-1'>
                  {profileData?.roles?.map((role, idx) => (
                    <Badge
                      key={idx}
                      color='secondary'
                      className='px-3 py-2 fs-13'
                    >
                      <i className='ri-shield-user-line me-2'></i> {role}
                    </Badge>
                  ))}
                </div>
              </Col>
            </Row>
          </div>

          {error && (
            <Alert color='danger' className='mb-4'>
              <strong>Error!</strong> {error}
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <div className='d-flex justify-content-end gap-2 mb-3'>
                <Button
                  color='success'
                  onClick={() => {
                    setFormErrors({});
                    setPasswordForm({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: '',
                    });
                    setShowPasswordModal(true);
                  }}
                >
                  <i className='ri-lock-password-line align-middle me-2'></i>{' '}
                  Change Password
                </Button>
              </div>

              <Row>
                <Col xxl={4}>
                  <Card>
                    <CardBody>
                      <h5 className='card-title mb-3'>Personal Info</h5>
                      <div className='d-flex align-items-center mb-3'>
                        <Table>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Username :
                            </th>
                            <td className='text-muted text-break'>
                              {profileData?.userName}
                            </td>
                          </tr>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Email :
                            </th>
                            <td className='text-muted text-break'>
                              {profileData?.email}
                            </td>
                          </tr>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Employee ID :
                            </th>
                            <td className='text-muted text-break'>
                              {profileData?.employeeNo || '-'}
                            </td>
                          </tr>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardBody>
                      <h5 className='card-title mb-4'>Assignment</h5>
                      <div className='d-flex align-items-center mb-3'>
                        <div className='flex-shrink-0 avatar-sm'>
                          <span className='avatar-title bg-light text-primary rounded-circle fs-3'>
                            <i className='ri-building-line'></i>
                          </span>
                        </div>
                        <div className='flex-grow-1 ms-3'>
                          <h6 className='fs-14 mb-1'>Company</h6>
                          <p className='text-muted mb-0'>
                            {profileData?.companyName || 'No Company Assigned'}
                          </p>
                          {profileData?.companyCode && (
                            <small className='text-muted'>
                              Code: {profileData.companyCode}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className='d-flex align-items-center'>
                        <div className='flex-shrink-0 avatar-sm'>
                          <span className='avatar-title bg-light text-primary rounded-circle fs-3'>
                            <i className='ri-community-line'></i>
                          </span>
                        </div>
                        <div className='flex-grow-1 ms-3'>
                          <h6 className='fs-14 mb-1'>Department</h6>
                          <p className='text-muted mb-0'>
                            {profileData?.department || 'No Department'}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xxl={8}>
                  <Card>
                    <CardBody>
                      <h5 className='card-title mb-3'>Profile Details</h5>
                      <Row>
                        <Col md={6}>
                          <div className='d-flex mt-4'>
                            <div className='flex-shrink-0 avatar-xs align-self-center me-3'>
                              <div className='avatar-title bg-light rounded-circle fs-16 text-primary'>
                                <i className='ri-user-2-line'></i>
                              </div>
                            </div>
                            <div className='flex-grow-1 overflow-hidden'>
                              <p className='mb-1 text-muted'>Display Name :</p>
                              <h6 className='text-truncate mb-0'>
                                {profileData?.displayName}
                              </h6>
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className='d-flex mt-4'>
                            <div className='flex-shrink-0 avatar-xs align-self-center me-3'>
                              <div className='avatar-title bg-light rounded-circle fs-16 text-primary'>
                                <i className='ri-briefcase-line'></i>
                              </div>
                            </div>
                            <div className='flex-grow-1 overflow-hidden'>
                              <p className='mb-1 text-muted'>Job Title :</p>
                              <h6 className='text-truncate mb-0'>
                                {profileData?.jobTitle || '-'}
                              </h6>
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className='d-flex mt-4'>
                            <div className='flex-shrink-0 avatar-xs align-self-center me-3'>
                              <div className='avatar-title bg-light rounded-circle fs-16 text-primary'>
                                <i className='ri-mail-line'></i>
                              </div>
                            </div>
                            <div className='flex-grow-1 overflow-hidden'>
                              <p className='mb-1 text-muted'>Email :</p>
                              <h6 className='text-truncate mb-0'>
                                {profileData?.email}
                              </h6>
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className='d-flex mt-4'>
                            <div className='flex-shrink-0 avatar-xs align-self-center me-3'>
                              <div className='avatar-title bg-light rounded-circle fs-16 text-primary'>
                                <i className='ri-map-pin-line'></i>
                              </div>
                            </div>
                            <div className='flex-grow-1 overflow-hidden'>
                              <p className='mb-1 text-muted'>Address :</p>
                              <h6 className='mb-0'>
                                {profileData?.address || '-'}
                              </h6>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal
        isOpen={showPasswordModal}
        toggle={() => setShowPasswordModal(false)}
        centered
      >
        <ModalHeader toggle={() => setShowPasswordModal(false)}>
          Change my password
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={e => e.preventDefault()}>
            <RequiredNote />
            <FormGrid columns={1}>
              <PasswordField
                label='Current password'
                name='currentPassword'
                required
                autoComplete='current-password'
                value={passwordForm.currentPassword}
                error={formErrors.currentPassword}
                onChange={e => updatePasswordField('currentPassword', e)}
              />
              <PasswordField
                label='New password'
                name='newPassword'
                required
                autoComplete='new-password'
                hint='At least 8 characters, one uppercase letter and one symbol'
                value={passwordForm.newPassword}
                error={formErrors.newPassword}
                onChange={e => updatePasswordField('newPassword', e)}
              />
              <PasswordField
                label='Confirm new password'
                name='confirmPassword'
                required
                autoComplete='new-password'
                value={passwordForm.confirmPassword}
                error={formErrors.confirmPassword}
                onChange={e => updatePasswordField('confirmPassword', e)}
              />
            </FormGrid>
          </Form>
        </ModalBody>
        <ModalFooter>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            onClick={() => setShowPasswordModal(false)}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            icon='ri-lock-password-line'
            onClick={handleChangePassword}
            loading={actionLoading}
          >
            Update password
          </ActionButton>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default UserProfile;
